import { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import { Container, Typography, Box } from '@mui/material';
import gsap from "gsap";

function CookiePolicy() {
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (pageRef.current) {
      gsap.fromTo(
        pageRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" }
      );
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Cookie Policy | CAMILLACINODESIGN&DEV</title>
        <meta name="description" content="Informativa sui cookie utilizzati dal sito camillacinodesign.dev ai sensi del Regolamento UE 2016/679 (GDPR)." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <Box
        ref={pageRef}
        sx={{
          minHeight: '100vh',
          pt: { xs: 14, md: 18 },
          pb: 10,
          backgroundColor: '#101010',
          color: '#fff',
        }}
      >
        <Container maxWidth="xl">
          <Typography
            variant="h2"
            sx={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.8rem' },
              mb: 2,
              background: 'linear-gradient(135deg, #048091, #7E82F7)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Cookie Policy
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontFamily: "'Space Grotesk', sans-serif",
              color: 'rgba(255,255,255,0.5)',
              mb: 6,
            }}
          >
            Ultimo aggiornamento: 19 agosto 2026
          </Typography>

          <Section title="1. Cosa sono i cookie">
            <p>
              I cookie sono piccoli file di testo che vengono memorizzati sul dispositivo dell'utente
              quando visita un sito web. Sono ampiamente utilizzati per far funzionare i siti web, migliorarne
              l'efficienza e fornire informazioni ai proprietari del sito.
            </p>
          </Section>

          <Section title="2. Titolare del trattamento">
            <p>
              Il titolare del trattamento è <strong>Camilla Cino - CAMILLACINODESIGN&DEV</strong>,
              con sede legale in via Per S. Felice 3, Camposanto (MO) 41031, P.IVA 04601200167.
            </p>
            <p>
              Email: <a href="mailto:info@camillacinodesigndev.it">info@camillacinodesigndev.it</a>
            </p>
          </Section>

          <Section title="3. Tipologie di cookie utilizzati">

            <SubSection title="3.1 Cookie tecnici (necessari)">
              <p>
                Sono essenziali per il corretto funzionamento del sito. Non richiedono il consenso dell'utente.
              </p>
              <Box sx={{
                backgroundColor: 'rgba(4, 128, 145, 0.08)',
                borderRadius: 2,
                p: 2,
                mb: 2,
                overflowX: 'auto',
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
                      <th style={{ textAlign: 'left', padding: '8px', color: '#048091' }}>Cookie</th>
                      <th style={{ textAlign: 'left', padding: '8px', color: '#048091' }}>Finalità</th>
                      <th style={{ textAlign: 'left', padding: '8px', color: '#048091' }}>Durata</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                      <td style={{ padding: '8px' }}>cookie_consent</td>
                      <td style={{ padding: '8px' }}>Memorizza le preferenze cookie dell'utente</td>
                      <td style={{ padding: '8px' }}>12 mesi</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                      <td style={{ padding: '8px' }}>theme</td>
                      <td style={{ padding: '8px' }}>Salva la preferenza di tema (chiaro/scuro)</td>
                      <td style={{ padding: '8px' }}>12 mesi</td>
                    </tr>
                  </tbody>
                </table>
              </Box>
            </SubSection>

            <SubSection title="3.2 Cookie analitici">
              <p>
                Utilizzati per raccogliere informazioni in forma aggregata sul numero di utenti e su come
                visitano il sito. Vengono installati solo previo consenso dell'utente.
              </p>
            </SubSection>

            <SubSection title="3.3 Cookie di terze parti">
              <p>Il sito può incorporare contenuti e servizi di terze parti che installano propri cookie:</p>
              <ul>
                <li>Al momento non sono presenti cookie di terze parti.</li>
              </ul>
            </SubSection>
          </Section>

          <Section title="4. Base giuridica">
            <ul>
              <li><strong>Cookie tecnici:</strong> installati senza consenso in quanto necessari al funzionamento del sito (art. 122 D.Lgs. 196/2003).</li>
              <li><strong>Cookie analitici e di terze parti:</strong> installati solo previo consenso esplicito dell'utente (art. 6.1.a GDPR).</li>
            </ul>
          </Section>

          <Section title="5. Gestione del consenso">
            <p>
              Al primo accesso al sito, l'utente visualizza un banner che consente di:
            </p>
            <ul>
              <li><strong>Accettare</strong> tutti i cookie.</li>
              <li><strong>Rifiutare</strong> i cookie non necessari.</li>
              <li><strong>Personalizzare</strong> le preferenze per categoria.</li>
            </ul>
            <p>
              Il consenso può essere revocato in qualsiasi momento cliccando sul pulsante
              "Gestisci Cookie" presente nel footer del sito, oppure cancellando i cookie
              direttamente dalle impostazioni del proprio browser.
            </p>
          </Section>

          <Section title="6. Come disabilitare i cookie dal browser">
            <p>
              L'utente può gestire i cookie anche tramite le impostazioni del proprio browser:
            </p>
            <ul>
              <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
              <li><a href="https://support.mozilla.org/it/kb/protezione-antitracciamento-avanzata-firefox-desktop" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
              <li><a href="https://support.apple.com/it-it/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Apple Safari</a></li>
              <li><a href="https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
            </ul>
            <p>
              La disabilitazione dei cookie tecnici potrebbe compromettere alcune funzionalità del sito.
            </p>
          </Section>

          <Section title="7. Conservazione dei dati">
            <p>
              I dati raccolti tramite cookie sono conservati per il periodo indicato nella tabella sopra.
              Alla scadenza, i cookie vengono automaticamente eliminati dal dispositivo dell'utente.
            </p>
          </Section>

          <Section title="8. Diritti dell'utente">
            <p>
              L'utente può esercitare i diritti previsti dagli artt. 15-22 del GDPR (accesso, rettifica,
              cancellazione, portabilità, opposizione) scrivendo a:{' '}
              <a href="mailto:info@camillacinodesigndev.it">info@camillacinodesigndev.it</a>
            </p>
          </Section>

          <Section title="9. Riferimenti normativi">
            <ul>
              <li>Regolamento UE 2016/679 (GDPR)</li>
              <li>D.Lgs. 196/2003 (Codice Privacy)</li>
              <li>D.Lgs. 101/2018</li>
              <li>Provvedimento del Garante Privacy del 10 giugno 2021 — "Linee guida cookie e altri strumenti di tracciamento"</li>
              <li>Direttiva 2002/58/CE (ePrivacy)</li>
            </ul>
          </Section>

          <Section title="10. Contatti">
            <Box
              sx={{
                backgroundColor: 'rgba(4, 128, 145, 0.1)',
                border: '1px solid rgba(4, 128, 145, 0.3)',
                borderRadius: 2,
                p: 3,
                mt: 2,
              }}
            >
              <p style={{ margin: 0 }}>
                <strong>Camilla Cino - CAMILLACINODESIGN&DEV</strong><br />
                Via Per S. Felice 3, Camposanto (MO) 41031<br />
                P.IVA: 04601200167<br />
                Email: <a href="mailto:info@camillacinodesigndev.it" style={{ color: '#048091' }}>info@camillacinodesigndev.it</a>
              </p>
            </Box>
          </Section>

        </Container>
      </Box>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 5 }}>
      <Typography
        variant="h5"
        sx={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 600,
          fontSize: { xs: '1.2rem', md: '1.4rem' },
          mb: 2,
          color: '#fff',
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: '0.95rem',
          lineHeight: 1.8,
          color: 'rgba(255,255,255,0.8)',
          '& p': { mb: 2, mt: 0 },
          '& ul': { pl: 3, mb: 2 },
          '& li': { mb: 1 },
          '& a': {
            color: '#048091',
            textDecoration: 'underline',
            '&:hover': { color: '#7E82F7' },
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="h6"
        sx={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 500,
          fontSize: '1rem',
          mb: 1,
          color: 'rgba(255,255,255,0.9)',
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default CookiePolicy;
